/**
 * TOTP verification handlers
 * Extracted from useLoginController.ts to reduce file size
 */

import { ValidationError } from "class-validator";
import { useRouter } from "next/router";
import Auth from "@Front/Api/Auth/Customer/Auth";
import { CustomerStore } from "@Front/Stores/CustomerStore";
import { TotpCodesReasons } from "le-coffre-resources/dist/Customer/TotpCodes";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { assertAccessCookieForContext } from "@Front/Utils/lecoffreSessionCookieHelpers";
import { redirectAfterLogin, extractHttpError } from "./loginErrorHandlers";
import { LoginStep } from "../loginSteps";
import {
	buildCustomerLoginAuthScopePayload,
	type CustomerAuthDisambiguation,
} from "@Front/Components/Layouts/Login/helpers/loginFolderUidHelpers";

export type HandleSuccessfulTotpVerificationParams = {
	values: { [key: string]: string };
	email: string | undefined;
	router: ReturnType<typeof useRouter>;
	loginFolderUid: string | undefined;
	loginOfficeUid: string | undefined;
	loginCustomerUid: string | undefined;
	authDisambiguation: CustomerAuthDisambiguation;
	setStep: (step: LoginStep) => void;
	setTotpCode: (code: string) => void;
	setValidationErrors: (errors: ValidationError[]) => void;
	refresh: () => Promise<void>;
};

/**
 * Handle successful TOTP verification
 */
export async function handleSuccessfulTotpVerification(params: HandleSuccessfulTotpVerificationParams): Promise<void> {
	const { values, email, router, loginFolderUid, loginOfficeUid, loginCustomerUid, authDisambiguation, setValidationErrors, refresh } = params;
	if (!email || !values["totpCode"]) {
		setValidationErrors([
			{
				property: "totpCode",
				constraints: {
					"400": "Email ou code TOTP manquant.",
				},
			},
		]);
		return;
	}
	const token = await Auth.getInstance().login({
		totpCode: values["totpCode"],
		email,
		...buildCustomerLoginAuthScopePayload(loginFolderUid, loginOfficeUid, authDisambiguation, loginCustomerUid),
	});
	CustomerStore.instance.connect(token.accessToken, token.refreshToken);

	await new Promise((resolve) => setTimeout(resolve, 100));

	try {
		await assertAccessCookieForContext("customer");
	} catch {
		LoggerService.error("LeCoffre customer access cookie still missing after TOTP connect retry");
		throw new Error("Erreur lors de la connexion");
	}

	await refresh();
	setValidationErrors([]);
	redirectAfterLogin(router);
}

export type HandleTotpVerificationResultParams = {
	res: { validCode: boolean; reason?: TotpCodesReasons };
	values: { [key: string]: string };
	email: string | undefined;
	router: ReturnType<typeof useRouter>;
	loginFolderUid: string | undefined;
	loginOfficeUid: string | undefined;
	loginCustomerUid: string | undefined;
	authDisambiguation: CustomerAuthDisambiguation;
	setStep: (step: LoginStep) => void;
	setTotpCode: (code: string) => void;
	setValidationErrors: (errors: ValidationError[]) => void;
	refresh: () => Promise<void>;
};

function applyTotpLoginFailureValidation(
	loginError: unknown,
	email: string | undefined,
	totpCode: string | undefined,
	setValidationErrors: HandleTotpVerificationResultParams["setValidationErrors"],
): void {
	const { http_status: loginHttpStatus, message: loginMessage } = extractHttpError(loginError);
	LoggerService.error("Customer login after TOTP verify failed", { error: loginError, email, totpCode });
	setValidationErrors([
		{
			property: "totpCode",
			constraints: {
				[String(loginHttpStatus ?? "unknown")]: loginMessage ?? "Une erreur est survenue lors de la connexion.",
			},
		},
	]);
}

/**
 * Handle TOTP verification result
 */
export function handleTotpVerificationResult(params: HandleTotpVerificationResultParams): void {
	const { res, values, email, router, loginFolderUid, loginOfficeUid, loginCustomerUid, authDisambiguation, setStep, setTotpCode, setValidationErrors, refresh } =
		params;
	if (!res.validCode || !values["totpCode"]) {
		return;
	}

	setTotpCode(values["totpCode"]);
	setValidationErrors([]);

	if (res.reason === TotpCodesReasons.FIRST_LOGIN) {
		setStep(LoginStep.NEW_PASSWORD);
		return;
	}
	if (res.reason === TotpCodesReasons.RESET_PASSWORD) {
		setStep(LoginStep.PASSWORD_FORGOTTEN);
		return;
	}
	void handleSuccessfulTotpVerification({
		values,
		email,
		router,
		loginFolderUid,
		loginOfficeUid,
		loginCustomerUid,
		authDisambiguation,
		setStep,
		setTotpCode,
		setValidationErrors,
		refresh,
	}).catch((loginError: unknown) => applyTotpLoginFailureValidation(loginError, email, values["totpCode"], setValidationErrors));
}

/**
 * Handle TOTP verification error
 */
export function handleTotpVerificationError(
	error: unknown,
	email: string | undefined,
	totpCodeValue: string | undefined,
	setValidationErrors: (errors: ValidationError[]) => void,
): void {
	const { http_status, message } = extractHttpError(error);
	LoggerService.error("verifyTotpCode failed", { error, email, totpCodeValue });
	setValidationErrors([
		{
			property: "totpCode",
			constraints: {
				[String(http_status ?? "unknown")]: message ?? "Une erreur est survenue.",
			},
		},
	]);
}

